import { createContext, type ReactNode, use, useCallback, useEffect, useState } from "react";

/**
 * Light and dark, with the design system doing the actual work.
 *
 * `@norican/react`'s `ThemeProvider` already carries both palettes — it sets
 * `data-theme` and every token resolves from there — so this file is only about
 * *choosing*, and about the two things a choice needs to be worth having:
 *
 * * **"System" is a real option, and the default.** Somebody who has told their
 *   OS they want light at 09:00 and dark at 18:00 has already answered this
 *   question; an app that ignores that answer is the reason people have to
 *   answer it again in every tool they open.
 * * **The choice survives a reload**, in localStorage, per browser. It is a
 *   display preference, not tenant state: it has no business in the database
 *   and nothing about it belongs in the audit log.
 *
 * Storage is wrapped in try/catch because a locked-down browser profile throws
 * on access rather than returning null, and a theme preference is never worth
 * a blank screen.
 */

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "hrops.theme";

function readStored(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "light" || value === "dark" || value === "system") {
      return value;
    }
  } catch {
    // Private window, or site data blocked. Fall through to the default.
  }
  return "system";
}

function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

type ThemeContextValue = {
  /** What the user picked, including "system". */
  choice: ThemeChoice;
  /** What that resolves to right now — the value handed to the design system. */
  theme: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeChoiceProvider({
  children,
}: {
  children: (theme: ResolvedTheme) => ReactNode;
}) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Follow the OS while the choice is "system": somebody whose machine flips to
  // dark in the evening expects the tab they left open to flip with it.
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) {
      return;
    }
    const onChange = () => setSystem(media.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Unstorable preference still applies for this session.
    }
  }, []);

  const theme: ResolvedTheme = choice === "system" ? system : choice;

  // `color-scheme` is what makes the browser's own furniture — scrollbars, form
  // controls, the flash before paint — match the app. Without it a light app
  // keeps dark scrollbars.
  useEffect(() => {
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  return <ThemeContext value={{ choice, theme, setChoice }}>{children(theme)}</ThemeContext>;
}

export function useThemeChoice(): ThemeContextValue {
  const value = use(ThemeContext);
  if (!value) {
    throw new Error("useThemeChoice must be used inside ThemeChoiceProvider");
  }
  return value;
}
